import React from "react";

/**
 * Spacing for the payroll pages.
 *
 * Deliberately inline styles rather than Tailwind utilities: the app loads a
 * precompiled App.css that does not define many of them (mb-4, mt-1…mt-5, gap-2,
 * col-span-2 …), so those silently render with no spacing at all.
 */
export const GAP = { xs: 8, sm: 12, md: 16, lg: 24, xl: 32 };

/**
 * Icon + label buttons, centred.
 *
 * The app's precompiled antd CSS predates the antd JS in use, so `.anticon` is
 * `inline-block` with no gap and an icon drifts below the label's baseline.
 * Spread this onto any <Button> that carries an icon.
 */
export const iconButton = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: GAP.xs,
};

/** An icon on its own (e.g. a remove control), centred in a fixed box. */
export const iconBox = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 32,
  height: 32,
  fontSize: 18,
  cursor: "pointer",
};

/** Props that put a modal in the middle of the screen with a scrollable body. */
export const centeredModal = {
  centered: true,
  destroyOnClose: true,
  styles: {
    body: { maxHeight: "calc(100vh - 220px)", overflowY: "auto", padding: "8px 4px" },
  },
};

export const card = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: GAP.lg,
};

/** Space between major blocks on a page. */
export const Section = ({ children, style }) => (
  <div style={{ marginBottom: GAP.lg, ...style }}>{children}</div>
);

/** Left group + right group on one row, wrapping on narrow screens. */
export const Toolbar = ({ left, right, style }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: GAP.md,
      marginBottom: GAP.lg,
      ...style,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: GAP.sm }}>
      {left}
    </div>
    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: GAP.sm }}>
      {right}
    </div>
  </div>
);

/** Tile colours, matching the pastel palette of the admin menu cards. */
export const TONES = {
  blue: { bg: "#eff6ff", border: "#bfdbfe", chip: "#dbeafe", ink: "#1d4ed8" },
  green: { bg: "#f0fdf4", border: "#bbf7d0", chip: "#dcfce7", ink: "#15803d" },
  rose: { bg: "#fff1f2", border: "#fecdd3", chip: "#ffe4e6", ink: "#be123c" },
  violet: { bg: "#f5f3ff", border: "#ddd6fe", chip: "#ede9fe", ink: "#6d28d9" },
};

/** Coloured summary tile: tinted card, label, value and an icon chip. */
export const Stat = ({ label, value, sub, tone = "blue", icon }) => {
  const t = TONES[tone] || TONES.blue;
  return (
    <div
      style={{
        ...card,
        padding: 20,
        background: t.bg,
        borderColor: t.border,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: GAP.md,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: t.ink,
            marginBottom: GAP.xs,
          }}
        >
          {label}
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, color: "#111827" }}>
          {value}
        </div>
        {sub ? (
          <div style={{ fontSize: 12, color: "#4b5563", marginTop: 6 }}>{sub}</div>
        ) : null}
      </div>
      {icon ? (
        <div
          style={{
            flex: "none",
            width: 40,
            height: 40,
            borderRadius: 12,
            background: t.chip,
            color: t.ink,
            fontSize: 20,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </div>
      ) : null}
    </div>
  );
};

/** A heading inside a drawer or modal form. */
export const FormHeading = ({ children, first }) => (
  <div
    style={{
      fontWeight: 600,
      margin: first ? `0 0 ${GAP.sm}px` : `${GAP.xl}px 0 ${GAP.sm}px`,
    }}
  >
    {children}
  </div>
);

/** One label/amount line in the payslip breakdown. */
export const AmountLine = ({ label, value, strong }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      gap: GAP.md,
      padding: "6px 0",
      fontWeight: strong ? 600 : 400,
      ...(strong
        ? { borderTop: "1px solid #e5e7eb", marginTop: GAP.xs, paddingTop: GAP.sm }
        : {}),
    }}
  >
    <span>{label}</span>
    <span style={{ whiteSpace: "nowrap" }}>{value}</span>
  </div>
);
